import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonText,
  IonTitle,
  IonToolbar,
  RefresherEventDetail,
  useIonAlert,
  useIonViewDidEnter,
  useIonViewWillEnter,
  IonIcon,
  IonModal,
  useIonViewDidLeave,
  useIonViewWillLeave,
} from "@ionic/react";
import {
  chevronBackOutline,
  chevronBackSharp,
  close,
  camera,
  pencil,
  eyeOutline,
} from "ionicons/icons";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactQuill from "react-quill";
import htmlParse from "html-react-parser";
import { useHistory, useParams, useLocation } from "react-router";
import OnlineStatus from "../components/OnlineStatus";
import TestEnvBanner from "../components/TestEnvBanner";
import {
  InspectionDef,
  PointDefType,
  PointValue,
  PointEqDescription,
  InspectionPointImage,
} from "../models/inspections/Inspections";
import PointAttachmentsComponent from "../components/PointAttachments";
import { decodeParam } from "../util/ApiHelper";
import "./Inspection.css";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import ApiError from "../components/ApiError";
import {
  getInspectionFromLocal,
  saveInspectionToLocal,
} from "../util/InspectionsHelper";
import { TestEquipment, useWorkOrderStore } from "../store/wo-order";
import useGetWorkOrder from "../hooks/useGetWorkOrder";
import useGetInspection from "../hooks/work-order/inspection/useGetInspection";
import useCompleteInspection from "../hooks/work-order/useCompleteInspection";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import { useOfflineStore } from "../store/offline-mode";
import useGetTestEquipmentListAndRule from "../hooks/work-order/inspection/useGetTestEquipmentListAndRule";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { pickImageFromGallery } from "../util/PhotoPicker";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { Filesystem } from "@capacitor/filesystem";
import { useEquipmentStore } from "../store/equipment";
import { getOptionByIdWithCORP } from "../api/TabwareOptions";
import { getEquipmentUdvList } from "../api/EquipmentMasters";

const Inspection: React.FC = () => {
  interface PointError {
    id: number;
    column: string;
    error: string;
  }
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const history = useHistory();
  const location = useLocation();
  const { inspectionId, offlineInspectionId, workOrderId, equipmentId } =
    useParams<{
      inspectionId: string;
      offlineInspectionId?: string;
      workOrderId?: string;
      equipmentId?: string;
    }>();

  const [errorState, setErrorState] = useState("");

  const [currentTestEquip, setCurrentTestEquip] = useState("[]");
  const [inspectionDefinition, setInspectionDefinition] =
    useState<InspectionDef>();
  const [showLoading, setShowLoading] = useState(false);
  const [equipmentUdvSetupValues, setEquipmentUdvSetupValues] = useState<
    string[]
  >([]);
  const [equipmentUdvValues, setEquipmentUdvValues] = useState<any[]>([]);
  const [pointDefs, setPointDefs] = useState<PointDefType[]>([]);
  const [pointVals, setPointVals] = useState<PointValue[]>([]);
  const [checkOverride, setCheckOverride] = useState<boolean>(false);
  const [disableInsp, setDisableInsp] = useState<boolean>(false);
  const [comments, setComments] = useState<string>("");
  const [pointEquipment, setPointEquipment] = useState<PointEqDescription[]>(
    []
  );
  const [validationErrors, setValidationErrors] = useState<PointError[]>([]);
  const [testEquipmentList, setTestEquipmentList] = useState<TestEquipment[]>(
    []
  );
  const [testEquipRule, setTestEquipRule] = useState<string>("");
  const [pointImages, setPointImages] = useState<{
    [key: string]: InspectionPointImage[];
  }>({});
  const [pointComments, setPointComments] = useState<{ [key: string]: string }>(
    {}
  );
  const pointImagesRef = useRef<{ [key: string]: InspectionPointImage[] }>({});
  const pointCommentsRef = useRef<{ [key: string]: string }>({});

  const offlineMessageShown = useRef(false);

  // User Story 889870: guard against losing in-progress inspection data when
  // the user navigates away (swipe-back, hardware/edge back, header back).
  // isDirtyRef  -> user has made unsaved edits since load/last save.
  // isCompletingRef -> a complete/submit is in flight, so the leave hook must
  //   not re-write a draft (completion clears the local draft on its own).
  const isDirtyRef = useRef(false);
  const isCompletingRef = useRef(false);

  useEffect(() => {
    pointImagesRef.current = pointImages;
  }, [pointImages]);
  useEffect(() => {
    pointCommentsRef.current = pointComments;
  }, [pointComments]);
  const latestInspectionIdRef = useRef<string>(inspectionId);
  useEffect(() => {
    latestInspectionIdRef.current = inspectionId;
  }, [inspectionId]);

  const isStaleInspection = useCallback(
    (targetInspectionId: string) =>
      latestInspectionIdRef.current !== targetInspectionId,
    []
  );

  // User Story 889870: mark the inspection as having unsaved user edits. Also
  // clears the "completing" flag so that editing again after a failed
  // completion re-enables autosave-on-leave.
  const markDirty = useCallback(() => {
    isDirtyRef.current = true;
    isCompletingRef.current = false;
  }, []);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [tempCommentInput, setTempCommentInput] = useState<string>("");
  const [currentCommentKey, setCurrentCommentKey] = useState<string>("");
  let displayWo = workOrderId ? decodeParam(workOrderId) : "";
  const equipmentIdFromParams = equipmentId ? decodeParam(equipmentId) : "";
  const isEquipmentContext = !!equipmentIdFromParams && !displayWo;
  const { completeInspection } = useCompleteInspection();
  const { getTestEquipmentListAndRule } = useGetTestEquipmentListAndRule();

  const { trigger } = useGetWorkOrder();
  const { getInspectionDefHierarchy, getInspection } = useGetInspection();
  const workOrder = useWorkOrderStore((state) =>
    state.getWorkOrderById(displayWo)
  );
  const setWoInspectionData = useWorkOrderStore(
    (state) => state.setWoInspectionData
  );
  const setEquipInspectionData = useEquipmentStore(
    (state) => state.setEquipInspectionData
  );

  const addInspectionToBeCompleted = useWoOfflineUpdatesStore(
    (state) => state.addInspectionToBeCompleted
  );
  const updateById = useWoOfflineUpdatesStore((state) => state.updateById);
  const updateStatusById = useWoOfflineUpdatesStore(
    (state) => state.updateStatusById
  );
  const makeOperationCompletedById = useWoOfflineUpdatesStore(
    (state) => state.makeOperationCompletedById
  );
  const isOffline = useOfflineStore((state) => state.isOffline);
  const equipment = workOrder?.Equipment ?? equipmentIdFromParams ?? "";
  const [presentAlert] = useIonAlert();

  const isEditingInOffline = Boolean(
    typeof offlineInspectionId === "string" && offlineInspectionId.trim()
  );

  const requiredColor = "danger";
  const inRefresh = useRef(false);
  const pointValMap = React.useMemo(() => {
    const map = new Map<string, PointValue>();
    pointVals.forEach((pv) => {
      map.set(`${pv.PointId}_${pv.ColumnCode}`, pv);
    });
    return map;
  }, [pointVals]);
  const safeJsonParse = useCallback(
    <T,>(jsonString: string | null | undefined, fallback: T): T => {
      try {
        if (!jsonString) return fallback;
        return JSON.parse(jsonString);
      } catch (e) {
        console.error("Failed to parse JSON:", e);
        return fallback;
      }
    },
    []
  );
  const addEquipmentIfNeeded = useCallback(
    (
      equipmentVal: Set<string>,
      equipmentValues: PointEqDescription[],
      itemDesc: Record<string, string>,
      eq: string | null
    ) => {
      const equipmentKey = eq ?? "No Equipment";
      if (!equipmentVal.has(equipmentKey)) {
        equipmentVal.add(equipmentKey);
        equipmentValues.push({
          Equipment: equipmentKey,
          Description:
            equipmentKey === "No Equipment" ? "" : itemDesc[equipmentKey] || "",
        });
      }
    },
    []
  );
  const processInspecError = useCallback(
    (status: number, errorData: any): string => {
      let errMsg = "";
      console.log("Inspection Original Error Message:", errorData);
      if (errorData) {
        if (status === 400 && typeof errorData === "object") {
          errMsg = errorData.Message ?? JSON.stringify(errorData);
        } else if (status === 500 && Array.isArray(errorData)) {
          errMsg = errorData[0]?.Text ?? JSON.stringify(errorData);
        } else {
          errMsg =
            typeof errorData === "string"
              ? errorData
              : JSON.stringify(errorData);
        }
        console.log("Inspection Processed Error Message:", errMsg);
        if (
          typeof errMsg === "string" &&
          errMsg.toLowerCase().includes("relogin")
        ) {
          console.log(
            "Inspection Error Message includes relogin:",
            errMsg.toLowerCase()
          );
          errMsg =
            translatedMessages["SessionExpired"]?.MessageText ||
            "Your session has expired. Please re-login.";
        }

        setErrorState(errMsg);
      }

      return errMsg;
    },
    [translatedMessages]
  );

  const getWoEquipment = useCallback(async () => {
    if (!displayWo) {
      return;
    }
    if (workOrder) return; //got it from the cache
    await trigger(displayWo);
  }, [displayWo, trigger, workOrder]);

  const loadTestEquipmentList = useCallback(async () => {
    if (testEquipmentList.length > 0 && testEquipRule) {
      return;
    }

    const cachedList = useWorkOrderStore.getState().testEquipmentList;
    const cachedRule = useWorkOrderStore.getState().testEquipRule;

    if (cachedList && cachedRule) {
      setTestEquipmentList(cachedList);
      setTestEquipRule(cachedRule);
      return;
    }

    const { equipList, equipRule } = await getTestEquipmentListAndRule();
    setTestEquipmentList(equipList || []);
    setTestEquipRule(equipRule || "");
  }, [getTestEquipmentListAndRule, testEquipmentList.length, testEquipRule]);

  const normalizeSetupToken = useCallback((value: string) => {
    return value
      .replace(/\u00A0/g, " ")
      .trim()
      .replace(/(^")|("$)/g, "")
      .replace(/\s+/g, " ")
      .toLowerCase();
  }, []);

  const parseSetupOptionValues = useCallback(
    (optionValue?: string | null) => {
      if (!optionValue) {
        return [];
      }

      return optionValue
        .split(/[\n,;|]/)
        .map((value) => value.replace(/\u00A0/g, " ").trim().replace(/(^")|("$)/g, ""))
        .filter(Boolean);
    },
    []
  );

  const getUdvDisplayValue = useCallback((udv: any) => {
    if (udv?.DateValue && udv.DateValue !== "0001-01-01T00:00:00") {
      return new Date(udv.DateValue).toLocaleDateString();
    }

    // Some UDV rows carry default Date/Numeric placeholders when no value is set.
    // In that case show blank instead of misleading "0".
    if (
      udv?.DateValue === "0001-01-01T00:00:00" &&
      (udv?.StringValue === null || String(udv.StringValue).trim() === "") &&
      (udv?.NumericValue === 0 ||
        udv?.NumericValue === null ||
        udv?.NumericValue === undefined)
    ) {
      return "";
    }

    if (udv?.StringValue !== null && udv?.StringValue !== undefined) {
      const value = String(udv.StringValue).trim();
      if (value) {
        return value;
      }
    }

    if (udv?.NumericValue !== null && udv?.NumericValue !== undefined) {
      return String(udv.NumericValue);
    }

    return "";
  }, []);

  const normalizeEquipmentUdvList = useCallback(
    (userValues: any[], setupValues: string[]) => {
      if (!Array.isArray(userValues) || setupValues.length === 0) {
        return [];
      }

      const setupTokens = setupValues.map((value) => normalizeSetupToken(value));
      const setupValueSet = new Set(setupTokens);

      const strictMatches = userValues.filter((udv) => {
        const udvTokens = [
          udv?.UdId,
          udv?.Element,
          udv?.Category,
          udv?.ObjectId,
          [udv?.Category, udv?.Subcategory, udv?.Element]
            .filter(Boolean)
            .join("|"),
          [udv?.Category, udv?.Element].filter(Boolean).join("|"),
        ]
          .filter(Boolean)
          .map((value) => normalizeSetupToken(String(value)));

        return udvTokens.some((token) => setupValueSet.has(token));
      });

      if (strictMatches.length > 0) {
        return strictMatches;
      }

      // Fallback: match setup labels against Element text when exact tokens do not align.
      return userValues.filter((udv) => {
        const elementToken = normalizeSetupToken(String(udv?.Element ?? ""));
        if (!elementToken) {
          return false;
        }
        return setupTokens.some(
          (setupToken) =>
            elementToken.includes(setupToken) || setupToken.includes(elementToken)
        );
      });
    },
    [normalizeSetupToken]
  );

  const loadEquipmentUdvSetup = useCallback(async () => {
    const equipmentNumber =
      (displayWo
        ? useWorkOrderStore.getState().getWorkOrderById(displayWo)?.Equipment
        : undefined) ??
      equipmentIdFromParams ??
      "";

    if (!equipmentNumber) {
      setEquipmentUdvSetupValues([]);
      setEquipmentUdvValues([]);
      return;
    }

    const [udvListResponse, setupOptionResponse] = await Promise.all([
      getEquipmentUdvList(equipmentNumber),
      getOptionByIdWithCORP("ICHeaderEquipmentUDV"),
    ]);

    const setupOptionPayload =
      setupOptionResponse.data?.Data ??
      setupOptionResponse.data ??
      setupOptionResponse;
    const setupValues = parseSetupOptionValues(
      setupOptionPayload?.PlantValue ?? setupOptionPayload?.DefaultValue ?? ""
    );
    setEquipmentUdvSetupValues(setupValues);

    if (setupValues.length > 0) {
      const udvPayload = udvListResponse.data ?? udvListResponse;
      const userValues = Array.isArray(udvPayload?.UserValues)
        ? udvPayload.UserValues
        : Array.isArray(udvPayload?.equipmentLocation?.UserValues)
        ? udvPayload.equipmentLocation.UserValues
        : Array.isArray(udvPayload?.Data?.UserValues)
        ? udvPayload.Data.UserValues
        : Array.isArray(udvPayload?.data?.UserValues)
        ? udvPayload.data.UserValues
        : Array.isArray(udvPayload)
        ? udvPayload
        : [];
      let filtered = normalizeEquipmentUdvList(userValues, setupValues);
      if (filtered.length === 0 && userValues.length > 0) {
        filtered = setupValues
          .map((token) => {
            const tokenNorm = normalizeSetupToken(token);
            return userValues.find((udv: any) => {
              const elementToken = normalizeSetupToken(String(udv?.Element ?? ""));
              const categoryToken = normalizeSetupToken(String(udv?.Category ?? ""));
              const fullToken = normalizeSetupToken(
                [udv?.Category, udv?.Subcategory, udv?.Element]
                  .filter(Boolean)
                  .join("|")
              );
              return (
                elementToken === tokenNorm ||
                categoryToken === tokenNorm ||
                fullToken === tokenNorm ||
                elementToken.includes(tokenNorm)
              );
            });
          })
          .filter(Boolean);
      }
      setEquipmentUdvValues(filtered);
    } else {
      setEquipmentUdvValues([]);
    }
  }, [
    displayWo,
    equipmentIdFromParams,
    normalizeEquipmentUdvList,
    normalizeSetupToken,
    parseSetupOptionValues,
  ]);

  const resetInspectionState = useCallback(() => {
    setErrorState("");
    setInspectionDefinition(undefined);
    setPointDefs([]);
    setPointVals([]);
    setCheckOverride(false);
    setDisableInsp(false);
    setComments("");
    setPointEquipment([]);
    setValidationErrors([]);
    setCurrentTestEquip("[]");
    setPointImages({});
    setPointComments({});
    // User Story 889870: a fresh load starts with no unsaved edits.
    isDirtyRef.current = false;
    isCompletingRef.current = false;
  }, []);

  const getNormalizedObjectId = useCallback(
    (objectId: string, resultId?: string | number): string => {
      if (!objectId) {
        return "";
      }
      const segments = objectId.split("|").filter(Boolean);
      const idsToStrip = new Set<string>();
      if (inspectionId) {
        idsToStrip.add(inspectionId);
      }
      if (resultId !== undefined && resultId !== null) {
        idsToStrip.add(String(resultId));
      }
      return segments.filter((segment) => !idsToStrip.has(segment)).join("|");
    },
    [inspectionId]
  );

  const getPointKey = useCallback(
    (pointId: number, columnIdentifier: string): string => {
      if (!columnIdentifier) {
        return `${pointId}_default`;
      }
      const parts = columnIdentifier.split("|");
      const columnCode = parts.at(-1) ?? columnIdentifier;
      return `${pointId}_${columnCode}`;
    },
    []
  );

  const getAnyCommentForPoint = useCallback(
    (pointId: number): string => {
      const prefix = `${pointId}_`;
      for (const [key, value] of Object.entries(pointComments)) {
        if (key.startsWith(prefix) && value?.trim()) {
          return value.trim();
        }
      }
      return "";
    },
    [pointComments]
  );

  const getPointImages = useCallback(
    (point?: {
      Images?: InspectionPointImage[];
      Image?: InspectionPointImage[];
    }) => {
      if (!point) {
        return [];
      }
      const images = point.Images ?? point.Image ?? [];
      return Array.isArray(images) ? images : [];
    },
    []
  );

  const processInspectionData = useCallback(
    (data: any, isCompleted: boolean) => {
      if (!isCompleted) {
        setCheckOverride(false);
      }
      let inspectionDef: InspectionDef;
      let inspectionResult: any = null;

      if (isCompleted && data?.InspectionResult?.Definition) {
        inspectionDef = data.InspectionResult.Definition;
        inspectionResult = data.InspectionResult;
      } else if (!isCompleted && data?.InspectionDefinition) {
        inspectionDef = data.InspectionDefinition;
      } else {
        console.error("Invalid inspection data structure");
        return;
      }

      const itemDesc: Record<string, string> = data.PointEqpList || {};
      const pDefs: PointDefType[] = [];
      const pVals: PointValue[] = [];
      const equipmentVal = new Set<string>();
      const equipmentValues: PointEqDescription[] = [];
      const extractedImages: { [key: string]: InspectionPointImage[] } = {};
      const extractedComments: { [key: string]: string } = {};

      inspectionDef.Points?.forEach((p0: PointDefType) => {
        const p = { ...p0 };

        if (p.ValueDataType === "b") p.DefaultValue = "false";
        if (p.RequiredFlag) p.Description = "* " + p.Description;

        let highLow = { high: Number.MAX_VALUE, low: Number.MIN_VALUE };
        const desired =
          p.Meta?.find((m) => m.MetaType === "desired")?.MetaValue ?? 0;

        p.Meta?.forEach((m) => {
          if (m.MetaType === "limit") {
            highLow.high =
              m.MetaSubtype === "high" ? m.MetaValue : highLow.high;
            highLow.low = m.MetaSubtype === "low" ? m.MetaValue : highLow.low;
            p.Limit = true;
          } else if (m.MetaType === "tolerance") {
            highLow.high =
              m.MetaSubtype === "high" ? m.MetaValue : highLow.high;
            highLow.low = m.MetaSubtype === "low" ? m.MetaValue : highLow.low;
            p.Tolerance = true;
          } else if (m.MetaType === "tolerance%") {
            highLow.high =
              m.MetaSubtype === "high"
                ? (1 + m.MetaValue * 0.01) * desired
                : highLow.high;
            highLow.low =
              m.MetaSubtype === "low"
                ? (1 - Math.abs(m.MetaValue) * 0.01) * desired
                : highLow.low;
            p.TolerancePercent = true;
          } else if (m.MetaType === "precision") {
            p.Precision = m.MetaValue;
          } else if (m.MetaType === "default") {
            p.DefaultValue = m.MetaSubtype;
          }
        });

        p.HighValue = highLow.high;
        p.LowValue = highLow.low;
        p.Equipment ??= "No Equipment";
        addEquipmentIfNeeded(
          equipmentVal,
          equipmentValues,
          itemDesc,
          p.Equipment
        );

        pDefs.push(p);

        p.Columns?.forEach((pc) => {
          let measureValue = "";
          if (p.RequiredFlag || p.ValueDataType !== "n") {
            measureValue = p.DefaultValue ?? "";
          }
          pVals.push({
            PointId: p.PointId,
            ObjectId: pc.ObjectId,
            ColumnCode: pc.ColumnCode,
            Measure: measureValue,
            FailedFlag: false,
          });
        });
      });

      if (isCompleted && inspectionResult?.Points) {
        inspectionResult.Points.forEach((p: PointValue, index: number) => {
          const existingIndex = pVals.findIndex(
            (pv) => pv.PointId === p.PointId && pv.ColumnCode === p.ColumnCode
          );
          if (existingIndex !== -1) {
            const normalizedObjectId = getNormalizedObjectId(
              p.ObjectId,
              p.ResultId ?? inspectionResult?.ResultId
            );
            pVals[existingIndex] = {
              PointId: p.PointId,
              ObjectId: normalizedObjectId,
              ColumnCode: p.ColumnCode,
              Measure: p.Measure ?? "",
              FailedFlag: p.FailedFlag ?? false,
            };
          }
          const pointImagesFromResult = getPointImages(p);
          if (pointImagesFromResult.length > 0) {
            const key = getPointKey(
              p.PointId,
              p.ColumnCode || p.ObjectId || ""
            );
            extractedImages[key] = normalizeCompletedInspectionImages(
              pointImagesFromResult
            );
          }
          if (p.Comments) {
            const key = getPointKey(
              p.PointId,
              p.ColumnCode || p.ObjectId || ""
            );
            extractedComments[key] = p.Comments;
          }
        });

        setComments(inspectionResult.Comments || "");
        setCurrentTestEquip(
          JSON.stringify(inspectionResult.TestEquipment || [])
        );
      }

      setPointEquipment(equipmentValues);
      setPointDefs(pDefs);
      setPointVals(pVals);
      setInspectionDefinition(inspectionDef);
      setPointImages(extractedImages);
      setPointComments(extractedComments);
      if (inspectionDef.InspectionType === "Calibration") {
        loadTestEquipmentList();
      }
    },
    [
      inspectionId,
      loadTestEquipmentList,
      addEquipmentIfNeeded,
      getPointKey,
      getNormalizedObjectId,
      getPointImages,
    ]
  );
  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    inRefresh.current = true;
    if (!disableInsp) {
      await loadEquipmentUdvSetup();
      await getInspectionDefHierarchy(inspectionId, displayWo, {
        isEquipmentContext,
      });
    }
    event.detail.complete();
    inRefresh.current = false;
  };

  const getMetaString = useCallback((meta: any[]): string => {
    return meta
      .map((metaValue) => {
        if (metaValue.MetaType === "precision") {
          return `${translations["lbl_digits_after_decimal"] || "Digits after decimal"}: ${metaValue.MetaValue} `;
        }
        const rawSubtype = `${metaValue.MetaSubtype ?? ""}`.trim();
        const isBlankSubtype =
          !rawSubtype || rawSubtype.toLowerCase() === "blank";
        const metaType = `${metaValue.MetaType ?? ""}`;
        const subtypeLabel = isBlankSubtype
          ? metaType.charAt(0).toUpperCase() + metaType.slice(1)
          : metaValue.MetaSubtype;
        return `${subtypeLabel}${
          metaValue.MetaType === "default" ? "" : ":" + metaValue.MetaValue
        }${metaValue.MetaType === "tolerance%" ? "% " : " "}`;
      })
      .join("");
  }, [translations]);

  const getMetaNotes = useCallback(
    (meta: any[]) => {
      return (
        <IonNote class="inspection-helper-note" slot="helper">
          {getMetaString(meta)}
        </IonNote>
      );
    },
    [getMetaString]
  );

  const normalizeCompletedInspectionImages = (
    images?: InspectionPointImage[]
  ): InspectionPointImage[] => {
    if (!Array.isArray(images)) {
      return [];
    }
    return images.map((image) => {
      const rawPayload =
        (image as any)?.InspectionPointImage ?? image?.ImageType ?? "";
      const normalizedPayload = buildDataUrlFromPayload(
        rawPayload,
        image.ImageExtension
      );
      return {
        ...image,
        ImageType: normalizedPayload,
        InspectionPointImage: normalizedPayload,
      } as InspectionPointImage;
    });
  };

  const stripInspectionPointData = useCallback(
    (images: InspectionPointImage[] = []): InspectionPointImage[] =>
      images.map((image) => {
        const { InspectionPointImage: _ignored, ...rest } = image;
        let imageTypeForApi = rest.ImageType || "";
        
        if (imageTypeForApi.startsWith("data:")) {
          const imageData = imageTypeForApi.split(";base64,");
          imageTypeForApi = imageData[1] || imageTypeForApi;
        }
        
        return {
          ...rest,
          ImageType: imageTypeForApi,
        } as InspectionPointImage;
      }),
    []
  );

  const validateInput = useCallback(
    (inValue: any, pointDef: PointDefType, col: any) => {
      markDirty();
      const pv: PointValue = {
        PointId: pointDef.PointId,
        ObjectId: col.ObjectId,
        ColumnCode: col.ColumnCode,
        Measure: inValue ?? "",
        FailedFlag: false,
      };

      let errors = validationErrors.filter((e) => e.column !== col.ObjectId);

      if (
        pointDef?.RequiredFlag &&
        (inValue === null || inValue === "" || inValue === undefined)
      ) {
        errors.push({
          id: pointDef.PointId,
          column: col.ObjectId,
          error: `${pointDef.Description}->${
            translations["lbl_value_required"] || "Value is Required."
          }`,
        });
      } else if (
        (col.ImpliesFailureFlag ||
          pointDef.Limit ||
          pointDef.Tolerance ||
          pointDef.TolerancePercent) &&
        pointDef &&
        inValue !== "" &&
        inValue !== null &&
        pointDef.Meta &&
        pointDef.ValueDataType === "n" &&
        !isNaN(+inValue)
      ) {
        //high/low
        if (pointDef.Precision !== undefined) {
          pv.Measure = Number.parseFloat(inValue).toFixed(pointDef.Precision);
        }
        if (
          +inValue > (pointDef.HighValue ?? Number.MAX_VALUE) ||
          +inValue < (pointDef.LowValue ?? Number.MIN_VALUE)
        ) {
          if (col.ImpliesFailureFlag) {
            pv.FailedFlag = true;
          }
          errors.push({
            id: pointDef.PointId,
            column: col.ObjectId,
            error: `${pointDef.Description}->${getMetaString(pointDef.Meta)}`,
          });
        }
      }
      //precision
      if (
        !col.ImpliesFailureFlag &&
        pointDef &&
        inValue !== "" &&
        inValue !== null &&
        pointDef.ValueDataType === "n" &&
        !isNaN(+inValue)
      ) {
        if (pointDef.Precision === 0) {
          pv.Measure = Math.round(Number.parseFloat(inValue)).toString();
        } else if (pointDef.Precision !== undefined) {
          pv.Measure = Number.parseFloat(inValue).toFixed(pointDef.Precision);
        }
      }

      if (
        pointDef.ValueDataType === "c" &&
        col.ColumnCode === "Indicated" &&
        inValue !== "" &&
        inValue !== null &&
        !isNaN(+inValue)
      ) {
        const previousPointApplied = pointVals.find(
          (p) => p.PointId === pointDef.PointId && p.ColumnCode === "Applied"
        );
        const previousPointTolerance = pointVals.find(
          (p) => p.PointId === pointDef.PointId && (p.ColumnCode === "Tolerance" || p.ColumnCode === "Tolerance%")
        );
        let appliedValue = 0;        
        let toleranceValue = 0;
        if (previousPointApplied){
          appliedValue = parseFloat(previousPointApplied.Measure);
        }
        if (previousPointTolerance) {
          if (previousPointTolerance.ColumnCode === "Tolerance") {
            toleranceValue = parseFloat(previousPointTolerance.Measure);
          } else if (previousPointTolerance.ColumnCode === "Tolerance%") {
            const tolerancePercent = parseFloat(previousPointTolerance.Measure);
            toleranceValue = (tolerancePercent / 100) * appliedValue;
          }
        }
        const lowerBound = appliedValue - toleranceValue;
        const upperBound = appliedValue + toleranceValue;
        if (!(appliedValue === 0 && toleranceValue === 0)) {
          if (+inValue < lowerBound || +inValue > upperBound) {
            pv.FailedFlag = true;
            errors.push({
              id: pointDef.PointId,
              column: col.ObjectId,
              error: `${pointDef.Description}-> Value is out of the acceptable range (${lowerBound.toFixed(2)} - ${upperBound.toFixed(2)}).`,
            });
          }
        }
      }

      setPointVals((prev) =>
        prev
          .filter(
            (p) => !(p.PointId === pv.PointId && p.ColumnCode === pv.ColumnCode)
          )
          .concat(pv)
      );
      setValidationErrors(errors);
    },
    [validationErrors, translations, getMetaString, markDirty]
  );

  const validateList = useCallback(
    (inValue: any, pointDef: PointDefType, col: any) => {
      markDirty();
      const pv: PointValue = {
        PointId: pointDef.PointId,
        ObjectId: col.ObjectId,
        ColumnCode: col.ColumnCode,
        Measure: inValue ?? "",
        FailedFlag: false,
      };

      let errors = validationErrors.filter((e) => e.column !== col.ObjectId);

      if (pointDef?.RequiredFlag && !inValue) {
        errors.push({
          id: pointDef.PointId,
          column: col.ObjectId,
          error: `${pointDef.Description}->${
            translations["lbl_value_required"] || "Value is Required."
          }`,
        });
      } else if (
        pointDef &&
        inValue &&
        pointDef.Meta
      ) {
        const metaVal = pointDef.Meta.find(
          (m) => m.MetaType === "valid_value" && m.MetaSubtype === inValue
        );
        if (metaVal?.MetaValue === 1) {
          pv.FailedFlag = true;
          errors.push({
            id: pointDef.PointId,
            column: col.ObjectId,
            error: `${pointDef.Description}->${metaVal.MetaSubtype}`,
          });
        }
      }

      setPointVals((prev) =>
        prev
          .filter(
            (p) => !(p.PointId === pv.PointId && p.ColumnCode === pv.ColumnCode)
          )
          .concat(pv)
      );
      setValidationErrors(errors);
    },
    [validationErrors, translations, markDirty]
  );

  const setCheckBoxValue = useCallback(
    (inValue: boolean, pointDef: PointDefType, col: any) => {
      markDirty();
      const pv: PointValue = {
        PointId: pointDef.PointId,
        ObjectId: col.ObjectId,
        ColumnCode: col.ColumnCode,
        Measure: String(inValue),
        FailedFlag: col.ImpliesFailureFlag ? inValue : false,
      };

      setPointVals((prev) =>
        prev
          .filter(
            (p) => !(p.PointId === pv.PointId && p.ColumnCode === pv.ColumnCode)
          )
          .concat(pv)
      );
    },
    [markDirty]
  );
  const saveInspectionResultsToLocal = useCallback(
    async (_e?: any, skipNotification: boolean = false) => {
      setShowLoading(true);
      const commentsEl = document.getElementById(
        "txtComments"
      ) as HTMLInputElement | null;
      const commentsVal = commentsEl?.value ?? comments;
      const enrichedPointVals = pointVals.map((point) => {
        const key = getPointKey(
          point.PointId,
          point.ColumnCode || point.ObjectId || ""
        );
        return {
          ...point,
          Images: stripInspectionPointData(pointImages[key] || []),
          Comments: pointComments[key] || "",
        };
      });
      const res = await saveInspectionToLocal(
        displayWo,
        equipment,
        inspectionId,
        enrichedPointVals,
        inspectionDefinition!,
        checkOverride,
        commentsVal,
        currentTestEquip
      );
      setShowLoading(false);
      // User Story 889870: a successful (draft) save clears unsaved state, so
      // leaving afterwards won't prompt or re-save.
      if (res) {
        isDirtyRef.current = false;
      }
      if (skipNotification) return;
      if (res) {
        presentAlert({
          header: translations["lbl_success"] || "Success",
          message:
            translations["msg_inspection_saved"] ||
            "Inspection successfully saved!",
          buttons: [translations["lbl_btn_ok"] || "OK"],
        });
      } else {
        presentAlert({
          header: translations["lbl_error"] || "Error",
          message:
            translations["msg_inspection_not_saved"] ||
            "Inspection was not saved",
          buttons: [translations["lbl_btn_ok"] || "OK"],
        });
      }
    },
    [
      comments,
      displayWo,
      equipment,
      inspectionId,
      pointVals,
      pointImages,
      pointComments,
      inspectionDefinition,
      checkOverride,
      currentTestEquip,
      presentAlert,
      translations,
      getPointKey,
      getNormalizedObjectId,
      stripInspectionPointData,
    ]
  );
  const completeInspectionResults = useCallback(async () => {
    // User Story 889870: a complete/submit is in flight. The leave hook must
    // not autosave a draft over a completed inspection (completion clears the
    // local draft itself, and the offline path saves its own draft on purpose).
    isCompletingRef.current = true;
    isDirtyRef.current = false;
    const commentsEl = document.getElementById(
      "txtComments"
    ) as HTMLInputElement | null;
    const commentsVal = commentsEl?.value ?? comments;
    const buildPointValues = (stripImages: boolean) =>
      pointVals.map((point) => {
        const key = getPointKey(
          point.PointId,
          point.ColumnCode || point.ObjectId || ""
        );
        const selectedImages = stripImages
          ? stripInspectionPointData(pointImages[key] || [])
          : pointImages[key] || [];
        return {
          ...point,
          Images: selectedImages,
          Comments: pointComments[key] || "",
        };
      });
    const payloadPointVals = buildPointValues(true);
    const displayPointVals = buildPointValues(false);
    const sanitizedPointImages = Object.entries(pointImages).reduce(
      (acc, [imgKey, images]) => {
        acc[imgKey] = stripInspectionPointData(images || []);
        return acc;
      },
      {} as { [key: string]: InspectionPointImage[] }
    );
    const data = {
      inspectionId,
      pointVals: payloadPointVals,
      inspectionDefinition: inspectionDefinition!,
      checkOverride,
      comments: commentsVal,
      currentTestEquip,
      woNumber: displayWo,
      pointImages: sanitizedPointImages,
      pointComments,
      equipmentId: equipment,
      isEquipmentContext,
    };

    if (isOffline) {
      console.log(
        `inspection data for offline with id '${offlineInspectionId}' & isEditing - ${isEditingInOffline}`
      );
      isEditingInOffline
        ? updateById(offlineInspectionId as string, data)
        : addInspectionToBeCompleted(data);
      await saveInspectionResultsToLocal(undefined, true);
      presentAlert({
        message:
          translatedMessages["MobOfflineInspComp"]?.MessageText ||
          "Inspection added in offline store for completion.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            handler: () => {
              if (isEquipmentContext) {
                const encodedEq = encodeURIComponent(
                  equipmentIdFromParams || equipment
                );
                history.replace(`/Equipment/${encodedEq}/inspections`);
              } else {
                history.replace(
                  `/workorder/${encodeURIComponent(displayWo)}/inspections`
                );
              }
            },
          },
        ],
      });
      return; //we will add it once user comes online
    }

    setShowLoading(true);
    const response = await completeInspection(data);

    if (response.isError) {
      const msg = processInspecError(response.status, response.data);
      if (offlineInspectionId) {
        updateStatusById(offlineInspectionId, "failed", [msg]);
      }
    } else {
      if (offlineInspectionId) {
        makeOperationCompletedById(offlineInspectionId);
      }

      const inspData = {
        isCompleted: true,
        inspectionResults: {
          InspectionResult: {
            Definition: inspectionDefinition,
            Points: displayPointVals,
            Comments: commentsVal,
            TestEquipment: safeJsonParse<TestEquipment[]>(currentTestEquip, []),
            ResultId: response.data?.ResultId,
            InspectedDatetime: new Date().toISOString(),
          },
        },
        inspectionId: inspectionId,
      };

      if (!isEquipmentContext && displayWo) {
        setWoInspectionData(displayWo, inspData);
      } else if (isEquipmentContext && equipment) {
        setEquipInspectionData(equipment, inspData);
      }

      presentAlert({
        message:
          translatedMessages["CompInsp"]?.MessageText ||
          "Inspection completed successfully",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            handler: () => {
              if (isEquipmentContext) {
                const encodedEq = encodeURIComponent(
                  equipmentIdFromParams || equipment
                );
                history.replace(`/Equipment/${encodedEq}/inspections`);
              } else {
                history.replace(
                  `/workorder/${encodeURIComponent(displayWo)}/inspections`
                );
              }
            },
          },
        ],
      });
      setErrorState("");
    }
    setShowLoading(false);
  }, [
    comments,
    inspectionId,
    pointVals,
    pointImages,
    pointComments,
    inspectionDefinition,
    checkOverride,
    currentTestEquip,
    displayWo,
    isOffline,
    isEditingInOffline,
    offlineInspectionId,
    updateById,
    addInspectionToBeCompleted,
    presentAlert,
    translatedMessages,
    setEquipInspectionData,
    translations,
    history,
    completeInspection,
    processInspecError,
    updateStatusById,
    makeOperationCompletedById,
    setWoInspectionData,
    safeJsonParse,
    getPointKey,
    getNormalizedObjectId,
    stripInspectionPointData,
    saveInspectionResultsToLocal,
  ]);

  const loadSavedAttachments = useCallback(async () => {
    try {
      const inspectionFromLocal = await getInspectionFromLocal(
        inspectionId,
        displayWo,
        equipment
      );

      if (inspectionFromLocal?.InspectionResultData) {
        const extractedImages: { [key: string]: InspectionPointImage[] } = {};
        const extractedComments: { [key: string]: string } = {};
        if (inspectionFromLocal.InspectionResultData.Points) {
          inspectionFromLocal.InspectionResultData.Points.forEach(
            (point: PointValue) => {
              const pointImagesFromResult = getPointImages(point);
              if (pointImagesFromResult.length > 0) {
                const key = getPointKey(
                  point.PointId,
                  point.ColumnCode || point.ObjectId || ""
                );
                extractedImages[key] = normalizeCompletedInspectionImages(
                  pointImagesFromResult
                );
              }
              if (point.Comments) {
                const key = getPointKey(
                  point.PointId,
                  point.ColumnCode || point.ObjectId || ""
                );
                extractedComments[key] = point.Comments;
              }
            }
          );
        }
        if (isStaleInspection(inspectionId)) {
          return;
        }
        if (
          Object.keys(extractedImages).length > 0 &&
          Object.keys(pointImagesRef.current).length === 0
        ) {
          setPointImages(extractedImages);
        }
        if (
          Object.keys(extractedComments).length > 0 &&
          Object.keys(pointCommentsRef.current).length === 0
        ) {
          setPointComments(extractedComments);
        }
      }
    } catch (error) {
      console.error("error", error);
    }
  }, [
    inspectionId,
    displayWo,
    equipment,
    getPointKey,
    isStaleInspection,
    getPointImages,
  ]);

  const requestPermissions = async () => {
    const permissions = await Camera.requestPermissions({
      permissions: ["camera"],
    });
    return permissions;
  };

  const checkPermissions = async () => {
    const permissions = await Camera.checkPermissions();
    return permissions;
  };

  const presentCustomPhotoSourceDialog = async () => {
    return new Promise<CameraSource | null>((resolve) => {
      presentAlert({
        header: translations["lbl_photo_source"] || "Photo Source",
        buttons: [
          {
            text: translations["lbl_camera"] || "Camera",
            handler: () => resolve(CameraSource.Camera),
          },
          {
            text: translations["lbl_photos"] || "Photos",
            handler: () => resolve(CameraSource.Photos),
          },
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "cancel",
            handler: () => resolve(null),
          },
        ],
      });
    });
  };

  const readDataAsBase64 = async (photo: any): Promise<string> => {
    if (isPlatform("hybrid")) {
      if (photo.path) {
        try {
          const file = await Filesystem.readFile({
            path: photo.path,
          });
          return file.data as string;
        } catch (error) {
          console.error("Error reading file from native path:", error);
        }
      }
      const resp = await fetch(photo.webPath);
      const blob = await resp.blob();
      return (await convertBlobToBase64(blob)) as string;
    } else {
      const resp = await fetch(photo.webPath);
      const blob = await resp.blob();
      return (await convertBlobToBase64(blob)) as string;
    }
  };

  const convertBlobToBase64 = (blob: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
  const buildDataUrlFromPayload = (
    payload: string,
    extension?: string
  ): string => {
    if (!payload) {
      return "";
    }
    if (
      payload.startsWith("data:") ||
      payload.startsWith("http") ||
      payload.startsWith("blob:")
    ) {
      return payload;
    }
    const sanitizedExt = extension?.toLowerCase().replace(/[^a-z0-9]/g, "");
    if (!sanitizedExt) {
      return payload;
    }
    return `data:image/${sanitizedExt};base64,${payload}`;
  };

  const getImageExtension = (photo: any, base64Data?: string): string => {
    const mimeMatch = base64Data?.startsWith("data:")
      ? base64Data.match(/data:image\/([^;]+)/)
      : null;
    if (mimeMatch?.[1]) {
      return mimeMatch[1].toLowerCase();
    }

    if (photo.format) {
      return photo.format.toLowerCase();
    }
    const pathMatch = (photo.path || photo.webPath)?.match(/\.([a-z]{3,4})$/i);
    return pathMatch?.[1]?.toLowerCase() ?? "";
  };

  const handleImageCapture = useCallback(
    async (pointId: number, columnId: string) => {
      const isWeb = Capacitor.getPlatform() === "web";
      const source = await presentCustomPhotoSourceDialog();
      if (source === null) return;

      let photo: any;
      try {
       
        if (source === CameraSource.Photos) {
          const picked = await pickImageFromGallery();
          if (!picked) return;

          const defaultFileName = "photo";
          presentAlert({
            message:
              translations["lbl_enter_file_name"] || "Please enter file name",
            backdropDismiss: false,
            buttons: [
              {
                text: translations["lbl_btn_ok"] || "OK",
                async handler(value) {
                  if (!!value && value[0] !== "") {
                    const key = getPointKey(pointId, columnId);
                    const imageExtension = picked.extension;
                    const altText = imageExtension
                      ? `${value[0]}.${imageExtension}`
                      : value[0];

                    const split = picked.dataUrl.split(";base64,");
                    const pureBase64 = split[1] || picked.dataUrl;

                    const structuredImage: InspectionPointImage = {
                      ImageType: pureBase64,
                      InspectionPointImage: picked.dataUrl,
                      ImageExtension: imageExtension,
                      AltText: altText,
                    };

                    markDirty();
                    setPointImages((prev) => ({
                      ...prev,
                      [key]: [...(prev[key] || []), structuredImage],
                    }));

                    return;
                  }
                  return false;
                },
              },
              {
                text: translations["lbl_btn_cancel"] || "Cancel",
                role: "Cancel",
              },
            ],
            inputs: [
              {
                placeholder: translations["lbl_file_name"] || "File Name",
                value: defaultFileName,
                attributes: {
                  maxlength: 100,
                },
              },
            ],
          });
          return;
        }

        if (!isWeb) {
      const permissions = await checkPermissions();
        if (permissions.camera !== "granted") {
            await requestPermissions();
          }
        }

        if (!isWeb && isPlatform("hybrid")) {
          await requestPermissions();
        }

        photo = await Camera.getPhoto({
          resultType: CameraResultType.Uri,
          allowEditing: false,
          quality: 100,
          source: source as CameraSource,
        });
      } catch (error) {
        console.error(error);
        return;
      }

      const defaultFileName = "photo";
      presentAlert({
        message:
          translations["lbl_enter_file_name"] || "Please enter file name",
        backdropDismiss: false,
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            async handler(value) {
              if (!!value && value[0] !== "") {
                const base64Data = await readDataAsBase64(photo);
                const key = getPointKey(pointId, columnId);
                const imageExtension = getImageExtension(photo, base64Data);
                const altText = `${value[0]}.${imageExtension}`;
                
                let pureBase64: string;
                let dataUrlForDisplay: string;
                
                if (base64Data.startsWith("data:")) {
                  const imageData = base64Data.split(";base64,");
                  pureBase64 = imageData[1] || base64Data;
                  dataUrlForDisplay = base64Data;
                } else {
                  pureBase64 = base64Data;
                  const sanitizedExt = imageExtension?.toLowerCase().replace(/[^a-z0-9]/g, "") || photo.format?.toLowerCase() || "";
                  if (sanitizedExt) {
                    dataUrlForDisplay = `data:image/${sanitizedExt};base64,${base64Data}`;
                  } else {
                    dataUrlForDisplay = base64Data;
                  }
                }

                const structuredImage: InspectionPointImage = {
                  ImageType: pureBase64,
                  InspectionPointImage: dataUrlForDisplay,
                  ImageExtension: imageExtension,
                  AltText: altText,
                };

                markDirty();
                setPointImages((prev) => ({
                  ...prev,
                  [key]: [...(prev[key] || []), structuredImage],
                }));

                return;
              }
              return false;
            },
          },
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "Cancel",
          },
        ],
        inputs: [
          {
            placeholder: translations["lbl_file_name"] || "File Name",
            value: defaultFileName,
            attributes: {
              maxlength: 100,
            },
          },
        ],
      });
    },
    [presentAlert, translations, getPointKey]
  );

  const handleCommentEntry = useCallback(
    async (pointId: number, columnId: string) => {
      const key = getPointKey(pointId, columnId);
      const currentComment = pointComments[key] || "";

      setCurrentCommentKey(key);
      setTempCommentInput(currentComment);
      setShowCommentModal(true);
    },
    [pointComments, getPointKey]
  );

  const handleCommentSave = useCallback(() => {
    markDirty();
    setPointComments((prev) => ({
      ...prev,
      [currentCommentKey]: tempCommentInput,
    }));
    setShowCommentModal(false);
    setTempCommentInput("");
    setCurrentCommentKey("");
  }, [currentCommentKey, tempCommentInput, markDirty]);

  useIonViewDidEnter(() => {
    if (isOffline && !isEditingInOffline) {
      const addedForCompletion = useWoOfflineUpdatesStore
        .getState()
        .hasInspectionAddedForCompletionInOffline?.(inspectionId, displayWo);

      if (addedForCompletion && !offlineMessageShown.current) {
        offlineMessageShown.current = true;
        presentAlert({
          message:
            translatedMessages["MobOfflineInspAddedForComp"]?.MessageText ||
            "Inspection already added in offline store for completion.",
          buttons: [
            {
              text: translations["lbl_btn_ok"] || "OK",
              handler: () => {
                history.goBack();
              },
            },
          ],
        });
      }
    }
  }, [isOffline, isEditingInOffline]);

  // ---- User Story 889870: unsaved-changes leave guard & draft autosave ----
  // Refs so the (stable) history.block callback always sees current state.
  const disableInspRef = useRef(disableInsp);
  useEffect(() => {
    disableInspRef.current = disableInsp;
  }, [disableInsp]);

  // isLeavePromptOpenRef: a leave prompt is already showing (don't stack).
  // bypassLeaveGuardRef: allow the navigation we ourselves start on "Leave".
  const isLeavePromptOpenRef = useRef(false);
  const bypassLeaveGuardRef = useRef(false);

  // Latest draft-save fn, reachable from the memoized prompt handler below.
  const saveDraftRef = useRef(saveInspectionResultsToLocal);
  useEffect(() => {
    saveDraftRef.current = saveInspectionResultsToLocal;
  }, [saveInspectionResultsToLocal]);

  // Save the current draft, then run proceed(). Saving happens BEFORE navigation
  // on purpose: navigating first triggers the location-change effect that resets
  // component state (clearing field values and the dirty flag), so a save after
  // that would persist an empty inspection. Awaiting here captures the filled
  // values while they are still in state.
  const saveDraftThen = useCallback((proceed: () => void) => {
    void (async () => {
      try {
        await saveDraftRef.current(undefined, true);
      } catch (error) {
        console.error("Failed to autosave inspection draft on leave.", error);
      }
      proceed();
    })();
  }, []);

  // Ask before leaving an inspection with unsaved edits. proceed() performs the
  // actual navigation. A clean or read-only/completed inspection leaves freely.
  const attemptLeave = useCallback(
    (proceed: () => void) => {
      if (!isDirtyRef.current || disableInspRef.current) {
        proceed();
        return;
      }
      if (isLeavePromptOpenRef.current) return;
      isLeavePromptOpenRef.current = true;
      presentAlert({
        header: translations["lbl_unsaved_changes"] || "Unsaved Changes",
        message:
          translatedMessages["MobInspUnsavedLeave"]?.MessageText ||
          translations["msg_inspection_unsaved_leave"] ||
          "You have unsaved changes. Your progress will be saved as a draft. Leave this inspection?",
        onDidDismiss: () => {
          isLeavePromptOpenRef.current = false;
        },
        buttons: [
          { text: translations["lbl_btn_cancel"] || "Cancel", role: "cancel" },
          {
            text: translations["lbl_btn_leave"] || "Leave",
            // Persist the draft first, then navigate.
            handler: () => saveDraftThen(proceed),
          },
        ],
      });
    },
    [presentAlert, translations, translatedMessages, saveDraftThen]
  );

  const attemptLeaveRef = useRef(attemptLeave);
  useEffect(() => {
    attemptLeaveRef.current = attemptLeave;
  }, [attemptLeave]);

  // Single navigation guard covering EVERY in-app exit from the inspection
  // page: header back, side-menu links, other in-app links, and browser/OS
  // back. It intercepts the underlying router history, so no separate
  // back-button or Android hardware-back handler is needed. Scoped to while the
  // view is active (registered on enter, removed on leave) so it can't affect
  // other pages Ionic keeps mounted in the background.
  const leaveBlockRef = useRef<null | (() => void)>(null);

  const registerLeaveGuard = useCallback(() => {
    leaveBlockRef.current?.();
    leaveBlockRef.current = history.block((location: any) => {
      if (bypassLeaveGuardRef.current) {
        bypassLeaveGuardRef.current = false;
        return; // allow the navigation we initiated after "Leave"
      }
      if (!isDirtyRef.current || disableInspRef.current) {
        return; // nothing unsaved -> allow
      }
      attemptLeaveRef.current(() => {
        bypassLeaveGuardRef.current = true;
        leaveBlockRef.current?.();
        leaveBlockRef.current = null;
        const target =
          (location?.pathname ?? "") +
          (location?.search ?? "") +
          (location?.hash ?? "");
        history.push(target);
      });
      return false; // block this attempt; navigation is driven by the prompt
    });
  }, [history]);

  const unregisterLeaveGuard = useCallback(() => {
    leaveBlockRef.current?.();
    leaveBlockRef.current = null;
  }, []);

  // Header back button just navigates; the guard above shows the prompt.
  const handleHeaderBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace(
        isEquipmentContext
          ? `/Equipment/${equipmentId}/inspections`
          : `/workorder/${workOrderId}`
      );
    }
  }, [history, isEquipmentContext, equipmentId, workOrderId]);

  useIonViewWillEnter(() => {
    registerLeaveGuard();
  });

  useIonViewDidLeave(() => {
    offlineMessageShown.current = false;
    unregisterLeaveGuard();
    bypassLeaveGuardRef.current = false;
    isLeavePromptOpenRef.current = false;
  });

  // Silently persist an in-progress draft on any exit that still has unsaved
  // edits, so an accidental navigation never discards work. The draft is
  // restored on re-open by useIonViewWillEnter (Status === "InProcess").
  useIonViewWillLeave(() => {
    if (
      isDirtyRef.current &&
      !isCompletingRef.current &&
      !disableInsp &&
      inspectionDefinition
    ) {
      void saveInspectionResultsToLocal(undefined, true);
    }
  });

  useEffect(() => {
    if (displayWo && !isEquipmentContext) {
      useWorkOrderStore.getState().setWoInspectionData(displayWo, {
        isCompleted: false,
        inspectionResults: undefined,
        inspectionDef: undefined,
        inspectionId: undefined,
      });
    }
    setCheckOverride(false);
    resetInspectionState();
  }, [
    inspectionId,
    location.pathname,
    displayWo,
    isEquipmentContext,
    resetInspectionState,
  ]);

  useIonViewWillEnter(() => {
    (async () => {
      const pathSegments = window.location.pathname.split("/");
      const freshInspectionId = isEditingInOffline
        ? inspectionId
        : pathSegments[pathSegments.length - 1];
      const currentInspectionId = freshInspectionId;

      // User Story 889870: route params (workOrderId/equipmentId) can be STALE
      // on the first ionViewWillEnter when Ionic reuses the cached inspection
      // page for a different owner that shares the same route pattern AND
      // inspection id -- e.g. a parent WO (EM1380) and its task WO (EM1380-01)
      // that copied the same inspection. Read the owner fresh from the URL, the
      // same way the inspection id is read above, so we load and restore the
      // correct WO/equipment (otherwise the previously-opened WO's draft/data
      // shows up until the user backs out and re-opens).
      const inspSegmentIndex = pathSegments.indexOf("inspection");
      const freshOwnerId =
        inspSegmentIndex > 0
          ? decodeParam(pathSegments[inspSegmentIndex - 1] ?? "")
          : "";
      if (!isEquipmentContext && freshOwnerId) {
        displayWo = freshOwnerId;
      }
      const effectiveEquipment =
        isEquipmentContext && freshOwnerId ? freshOwnerId : equipment;

      const defaultWorkOrder = useWorkOrderStore.getState().workOrders[0]; // First work order as default
      if (defaultWorkOrder && !displayWo && !isEquipmentContext) {
        const defaultWo = defaultWorkOrder.CfWorkOrder ?? "";
        displayWo = defaultWo;
      }
      setShowLoading(true);
      try {
        await getWoEquipment();
        await loadEquipmentUdvSetup();
        if (isStaleInspection(currentInspectionId) && !isEditingInOffline) {
          setShowLoading(false);
          return;
        }
        const savedInspection = await getInspectionFromLocal(
          currentInspectionId,
          displayWo,
          effectiveEquipment
        );
        if (isStaleInspection(currentInspectionId) && !isEditingInOffline) {
          setShowLoading(false);
          return;
        }
        if (savedInspection?.Status === "InProcess") {
          const inspectionResult = savedInspection.InspectionResultData;
          const dataForProcessing = {
            InspectionResult: {
              ...inspectionResult,
              Definition: inspectionResult.Definition,
              Points: inspectionResult.Points,
              Comments: inspectionResult.Comments || "",
              TestEquipment: inspectionResult.TestEquipment || [],
            },
            PointEqpList: savedInspection.PointEqpList || {},
          };
          const inspData = {
            isCompleted: false,
            inspectionResults: dataForProcessing,
            inspectionId: currentInspectionId,
          };
          if (!isEquipmentContext && displayWo) {
            setWoInspectionData(displayWo, inspData);
          } else if (isEquipmentContext && effectiveEquipment) {
            setEquipInspectionData(effectiveEquipment, inspData);
          }
          processInspectionData(dataForProcessing, true);
          setShowLoading(false);
          return;
        }

        let data: any = undefined;
        let isCompleted = false;

        //get it form the cache (only for work-order inspections)
        const woStore = useWorkOrderStore.getState();
        const equipmentStore = useEquipmentStore.getState();

        let inspectionData: any | undefined;
        if (!isEquipmentContext && displayWo) {
          inspectionData = woStore.getInspectionDataForWo(
            displayWo,
            currentInspectionId
          );
        } else if (isEquipmentContext && effectiveEquipment) {
          inspectionData = equipmentStore.getInspectionDataForEquip(
            effectiveEquipment,
            currentInspectionId
          );
          if (inspectionData?.isCompleted) {
            inspectionData = undefined;
          }
        }

        if (inspectionData) {
          data =
            inspectionData.inspectionResults ?? inspectionData.inspectionDef;
          isCompleted = !isEquipmentContext && inspectionData.isCompleted;
        } else {
          const result = await getInspection(displayWo, currentInspectionId, {
            isEquipmentContext,
            equipmentId: effectiveEquipment,
          });
          isCompleted = !isEquipmentContext && result.isCompleted;
          data = result.data;

          if (result.hasError) {
            processInspecError(result.status, data);
            return;
          }
        }

        // For equipment context, we always treat the inspection as "not completed"
        // so that the UI stays editable and no "already completed" message is shown.
        processInspectionData(data, isCompleted);
        if (!isEquipmentContext && isCompleted) {
          setDisableInsp(true);
          presentAlert({
            header: translations["lbl_completed"] || "Completed",
            message:
              translatedMessages["CompInspExist"]?.MessageText ||
              translations["msg_completed_inspection_exists"] ||
              "Completed inspection already exists for this work order.",
            buttons: [translations["lbl_btn_ok"] || "OK"],
          });
        }
      } catch (error) {
        console.error("Error loading inspection data:", error);
        processInspecError(500, "An unexpected error occurred");
      } finally {
        setShowLoading(false);
      }
    })();
  });

  useEffect(() => {
    loadSavedAttachments();
  }, [loadSavedAttachments]);

  useEffect(() => {
    if (pointDefs.length === 0 || pointVals.length === 0) {
      return;
    }
    const errors: PointError[] = [];

    pointVals.forEach((pv) => {
      const pointDef = pointDefs.find((pd) => pd.PointId === pv.PointId);
      if (!pointDef) return;
      if (
        pointDef.RequiredFlag &&
        (pv.Measure === null || pv.Measure === "" || pv.Measure === undefined)
      ) {
        errors.push({
          id: pv.PointId,
          column: pv.ObjectId,
          error: `${pointDef.Description}->${
            translations["lbl_value_required"] || "Value is Required."
          }`,
        });
      }
    });
    setValidationErrors(errors);
  }, [pointVals, pointDefs, translations]);

  const renderInputField = useCallback(
    (p: PointDefType, col: any, j: number) => {
      const pointVal = pointValMap.get(`${p.PointId}_${col.ColumnCode}`);

      if (p.ValueDataType === "b") {
        return (
          <IonItem
            key={`${col.ObjectId}.${j}`}
            color={p.RequiredFlag ? requiredColor : undefined}
          >
            <IonCheckbox
              slot="end"
              name={col.ObjectId}
              disabled={disableInsp}
              checked={pointVal?.Measure === "true"}
              onIonChange={(ev) => setCheckBoxValue(ev.detail.checked, p, col)}
            />
          </IonItem>
        );
      }

      if (p.ValueDataType === "l") {
        return (
          <IonItem key={`${col.ObjectId}.${j}`}>
            <IonSelect
              name={col.ObjectId}
              className={
                pointVal?.FailedFlag
                  ? "inspection-ion-select-fail"
                  : "inspection-ion-select"
              }
              interface="popover"
              disabled={disableInsp}
              placeholder={
                translations["lbl_select list"] || "select from list"
              }
              value={pointVal?.Measure}
              onIonChange={(ev) => validateList(ev.detail.value, p, col)}
            >
              {p.Meta?.map((mv, index) => (
                <IonSelectOption
                  key={`${index}${mv.MetaSubtype}`}
                  value={mv.MetaSubtype}
                >
                  {mv.MetaSubtype}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
        );
      }

      const isIndicatedFieldEditable =
        p.ValueDataType === "c" &&
        col.ColumnCode === "Indicated" &&
        pointVals.some(
          (pv) =>
            pv.PointId === p.PointId &&
            pv.ColumnCode === "Applied" &&
            pv.Measure
        ) &&
        pointVals.some(
          (pv) =>
            pv.PointId === p.PointId &&
            (pv.ColumnCode === "Tolerance" || pv.ColumnCode === "Tolerance%") &&
            pv.Measure
        );
      
      return (
        <IonItem key={`${col.ObjectId}.${j}`}>
          <IonInput
            className={
              pointVal?.FailedFlag
                ? "inspection-gridcol-inputcol-fail"
                : "inspection-gridcol-inputcol"
            }
            name={col.ObjectId}
            disabled={disableInsp || (p.ValueDataType === "c" && col.ColumnCode === "Indicated" && !isIndicatedFieldEditable)}
            required={p.RequiredFlag}
            type={p.ValueDataType === "n" ? "number" : "text"}
            inputMode={p.ValueDataType === "n" ? "decimal" : "text"}
            placeholder={String(p.DefaultValue ?? "")}
            min={p.ValueDataType === "n" ? 0 : undefined}
            max={p.ValueDataType === "n" ? p.HighValue : undefined}
            value={pointVal?.Measure}
            onIonChange={(ev) => validateInput(ev.detail.value ?? "", p, col)}
          />
          {p.ValueDataType !== "l" && p.Meta && getMetaNotes(p.Meta)}
        </IonItem>
      );
    },
    [
      pointValMap,
      disableInsp,
      requiredColor,
      setCheckBoxValue,
      validateList,
      validateInput,
      translations,
      getMetaNotes,
    ]
  );

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [fullDescription, setFullDescription] = useState("");
  const [fullLongDescription, setFullLongDescription] = useState("");
  const [showLongDescriptionModal, setShowLongDescriptionModal] = useState(false);

  const getPlainText = useCallback((text: string): string => {
    if (!text) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || "";
  }, []);

  const truncateText = useCallback(
    (text: string, maxLength: number = 150): string => {
      if (!text) return text;
      const plainText = getPlainText(text);
      if (plainText.length <= maxLength) return text;
      const truncatedPlainText = plainText.substring(0, maxLength) + "...";
      return truncatedPlainText;
    },
    [getPlainText]
  );

  const handleShowFullDescription = useCallback((description: string) => {
    setFullDescription(description);
    setShowDescriptionModal(true);
  }, []);

  const handleShowFullLongDescription = useCallback((longDescription: string) => {
    setFullLongDescription(longDescription);
    setShowLongDescriptionModal(true);
  }, []);

  const longDescriptionModalBreakpoint = useMemo(() => {
    if (!fullLongDescription) {
      return 0.5;
    }
    const plainText = getPlainText(fullLongDescription);
    return plainText.length > 120 ? 0.8 : 0.5;
  }, [fullLongDescription, getPlainText]);

  const longDescriptionModalBreakpoints = useMemo(() => {
    if (!fullLongDescription) {
      return [0, 0.5, 0.8];
    }
    const plainText = getPlainText(fullLongDescription);
    if (plainText.length <= 120) {
      return [0, 0.5, 0.8];
    }
    return [0, 0.5, 0.8, 1];
  }, [fullLongDescription, getPlainText]);

  const truncateHtmlContent = useCallback(
    (htmlContent: string, maxLength: number = 150): { truncated: string; isTruncated: boolean } => {
      if (!htmlContent) return { truncated: "", isTruncated: false };
      const plainText = getPlainText(htmlContent);

      if (plainText.length <= maxLength) {
        return { truncated: htmlContent, isTruncated: false };
      }
      const truncatedPlainText = plainText.substring(0, maxLength) + "...";
      return { truncated: truncatedPlainText, isTruncated: true };
    },
    [getPlainText]
  );

  const renderPointRows = useCallback(
    (pointsToRender: PointDefType[]) => {
      return pointsToRender
        .slice()
        .sort((a, b) => a.RowOrder - b.RowOrder)
        .map((p, i) => (
          <Fragment key={`fragment-${p.PointId}-${i}`}>
            <IonRow key={`${p.PointId}.category`}>
              <IonCol>{p.Category}</IonCol>
            </IonRow>
            <IonRow key={`${p.PointId}.header`}>
              <IonCol className="inspection-gridcol-header">{p.Label}</IonCol>
              {(p.Columns ?? [])
                .slice()
                .sort((a, b) => a.ColumnOrder - b.ColumnOrder)
                .map((col) => (
                  <IonCol
                    className="inspection-gridcol-header-input"
                    key={col.ObjectId}
                  >
                    {col.ColumnCode === "_Result" ? "-" : col.ColumnCode}
                  </IonCol>
                ))}
            </IonRow>

            <IonRow
              key={`${p.PointId}.actions`}
              className="inspection-action-buttons-row"
            >
              {(() => {
                const hasFailedForPoint = (p.Columns ?? []).some((col) => {
                  const pv = pointValMap.get(
                    `${p.PointId}_${col.ColumnCode}`
                  );
                  return pv?.FailedFlag;
                });
                const pointComment = getAnyCommentForPoint(p.PointId);
                const isCommentMissingForFailedPoint =
                  hasFailedForPoint && !pointComment;
                const pencilColor = isCommentMissingForFailedPoint
                  ? "var(--ion-color-danger)"
                  : "var(--ion-color-primary)";

                return (
              <IonCol
                style={{
                  display: "flex",
                  textAlign: "right",
                  justifyContent: "flex-end",
                }}
              >
                <IonButton
                  fill="clear"
                  size="small"
                  className="inspection-action-button"
                  onClick={() =>
                    handleImageCapture(
                      p.PointId,
                      p.Columns?.[0]?.ObjectId || ""
                    )
                  }
                  disabled={disableInsp}
                  style={{
                    color: "var(--ion-color-primary)",
                    "--color": "var(--ion-color-primary)",
                  }}
                >
                  <IonIcon icon={camera} size="small"></IonIcon>
                </IonButton>
                <IonButton
                  fill="clear"
                  size="small"
                  className="inspection-action-button"
                  onClick={() =>
                    handleCommentEntry(
                      p.PointId,
                      p.Columns?.[0]?.ObjectId || ""
                    )
                  }
                  disabled={disableInsp}
                  style={{
                    color: pencilColor,
                    "--color": pencilColor,
                  }}
                >
                  <IonIcon icon={pencil} size="small"></IonIcon>
                </IonButton>
              </IonCol>
                );
              })()}
            </IonRow>

            <IonRow key={`${p.PointId}.content`}>
              <IonCol>
                <IonLabel
                  color={p.RequiredFlag ? requiredColor : undefined}
                  class="ion-text-wrap"
                >
                  <IonText>
                    {truncateText(p.Description)}
                    {p.Description &&
                      getPlainText(p.Description).length > 150 && (
                        <IonIcon
                          icon={eyeOutline}
                          size="medium"
                          className="inspection-eye-icon"
                          onClick={() =>
                            handleShowFullDescription(p.Description)
                          }
                        />
                      )}
                  </IonText>

                  {p.LongDescription && (() => {
                    const longDesc = p.LongDescription;
                    const { truncated, isTruncated } = truncateHtmlContent(
                      longDesc,
                      150
                    );
                    return (
                      <IonText className="inspection-long-description">
                        {isTruncated ? (
                          <IonText>
                            {truncated}
                            {longDesc && (
                              <IonIcon
                                icon={eyeOutline}
                                size="medium"
                                className="inspection-eye-icon"
                                onClick={() => {
                                  handleShowFullLongDescription(longDesc);
                                }}
                              />
                            )}
                          </IonText>
                        ) : (
                          htmlParse(longDesc)
                        )}
                      </IonText>
                    );
                  })()}
                </IonLabel>
              </IonCol>
              {(p.Columns ?? [])
                .slice()
                .sort((a, b) => a.ColumnOrder - b.ColumnOrder)
                .map((col, j) => (
                  <IonCol
                    key={`${col.ObjectId}.${j}`}
                    className="inspection-gridcol-inputcol"
                  >
                    {renderInputField(p, col, j)}
                  </IonCol>
                ))}
            </IonRow>

            <IonRow key={`${p.PointId}.attachments`}>
              <IonCol>
                <PointAttachmentsComponent
                  pointId={p.PointId}
                  columnId={p.Columns?.[0]?.ObjectId || ""}
                  pointImages={pointImages}
                  pointComments={pointComments}
                  onImagesChange={(imgs) => {
                    markDirty();
                    setPointImages(imgs);
                  }}
                  pointDefs={pointDefs}
                  disableInsp={disableInsp}
                />
              </IonCol>
            </IonRow>
          </Fragment>
        ));
    },
    [
      renderInputField,
      requiredColor,
      truncateText,
      getPlainText,
      truncateHtmlContent,
      handleShowFullDescription,
      handleShowFullLongDescription,
      pointImages,
      pointComments,
      disableInsp,
      pointDefs,
      handleImageCapture,
      handleCommentEntry,
      pointValMap,
      getPointKey,
    ]
  );

  const isCalibrationInspection = useMemo(
    () => inspectionDefinition?.InspectionType === "Calibration",
    [inspectionDefinition?.InspectionType]
  );

  const hasRequiredErrors = useMemo(
    () =>
      validationErrors.some((e) =>
        e.error.toLowerCase().includes("value is required")
      ),
    [validationErrors]
  );

  const hasFailedPoints = useMemo(
    () => pointVals.some((p) => p.FailedFlag),
    [pointVals]
  );

  const hasFailedPointsWithoutComment = useMemo(() => {
    const seen: Record<string, true> = {};
    const failedPointIds: number[] = [];
    for (const pv of pointVals) {
      if (!pv.FailedFlag) continue;
      const key = String(pv.PointId);
      if (seen[key]) continue;
      seen[key] = true;
      failedPointIds.push(pv.PointId);
    }

    for (const pointId of failedPointIds) {
      if (!getAnyCommentForPoint(pointId)) {
        return true;
      }
    }
    return false;
  }, [pointVals, getAnyCommentForPoint]);

  const isCompleteDisabled = useMemo(
    () =>
      disableInsp ||
      (hasFailedPoints && !checkOverride) ||
      hasFailedPointsWithoutComment ||
      (Boolean(testEquipRule) && currentTestEquip === "[]") ||
      hasRequiredErrors,
    [
      disableInsp,
      hasFailedPoints,
      hasFailedPointsWithoutComment,
      checkOverride,
      testEquipRule,
      currentTestEquip,
      hasRequiredErrors,
    ]
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {/* User Story 889870: custom back so we can prompt on unsaved edits
                (replaces IonBackButton, whose navigation we can't intercept). */}
            <IonButton fill="clear" onClick={handleHeaderBack}>
              <IonIcon
                slot="icon-only"
                ios={chevronBackOutline}
                md={chevronBackSharp}
              />
            </IonButton>
          </IonButtons>
          <IonTitle>
            {translations["lbl_category_inspection"] || "Inspections"}
          </IonTitle>
        </IonToolbar>
        <TestEnvBanner />
        <OnlineStatus />
        <ApiError errorData={errorState} />
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonLoading
          cssClass="my-custom-class"
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
        />
        {
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>{displayWo}</IonCardTitle>
              <IonCardSubtitle>{`${
                inspectionDefinition?.InspectionType ?? ""
              } - ${inspectionDefinition?.Description ?? ""} -${
                inspectionDefinition?.InspectionId ?? ""
              }`}</IonCardSubtitle>
              <IonCardSubtitle>{`${
                translations["lbl_category_equipment"] || "Equipment"
              }: ${equipment}`}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonAccordionGroup>
                {equipmentUdvSetupValues.length > 0 && (
                  <IonAccordion value="equipment-udv">
                    <IonItem slot="header">
                      <IonLabel>
                        {translations["lbl_equipment_udv"] ||
                          "Equipment UD Values"}
                      </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                      <IonList lines="full">
                        {equipmentUdvValues.length > 0 ? (
                          equipmentUdvValues.map((udv, index) => (
                            <IonItem
                              key={`${udv.UdId || udv.ObjectId || udv.Element}-${index}`}
                            >
                              <IonLabel>
                                <p>{udv.Element}</p>
                                <h3>{getUdvDisplayValue(udv)}</h3>
                              </IonLabel>
                            </IonItem>
                          ))
                        ) : (
                          <IonItem>
                            <IonLabel>
                              <p>
                                {translations["lbl_no_data"] ||
                                  "No equipment UD values found"}
                              </p>
                            </IonLabel>
                          </IonItem>
                        )}
                      </IonList>
                    </div>
                  </IonAccordion>
                )}
                <IonAccordion
                  disabled={inspectionDefinition?.Instructions == null}
                  value="instructions"
                >
                  <IonItem slot="header">
                    <IonLabel>
                      {translations["lbl_category_instructions"] ||
                        "Instructions"}
                    </IonLabel>
                  </IonItem>
                  <div className="ion-padding" slot="content">
                    <ReactQuill
                      readOnly
                      value={inspectionDefinition?.Instructions}
                      modules={{ toolbar: [[]] }}
                    />
                  </div>
                </IonAccordion>
                <IonAccordion
                  disabled={
                    inspectionDefinition?.MaterialRequirements == null ||
                    inspectionDefinition?.MaterialRequirements === "NULL"
                  }
                  value="material"
                >
                  <IonItem slot="header">
                    <IonLabel>
                      {translations["lbl_material"] || "Material"}
                    </IonLabel>
                  </IonItem>
                  <div className="ion-padding" slot="content">
                    <ReactQuill
                      readOnly
                      value={inspectionDefinition?.MaterialRequirements}
                      modules={{ toolbar: [[]] }}
                    />
                  </div>
                </IonAccordion>
              </IonAccordionGroup>
            </IonCardContent>
          </IonCard>
        }
        {inspectionDefinition?.Elements &&
          inspectionDefinition.Elements.length > 0 && (
            <IonCard>
              {inspectionDefinition.Elements.map((element: any) => (
                <IonItem key={`${element.FieldLabel}`}>
                  <IonTextarea
                    autoGrow
                    readonly
                    value={`${element.FieldLabel}: ${element.FieldValue}`}
                  />
                </IonItem>
              ))}
            </IonCard>
          )}

        {isCalibrationInspection && (
          <IonCard>
            <IonList>
              <IonItem>
                <IonLabel
                  color={testEquipRule ? requiredColor : undefined}
                  position="floating"
                >
                  Testing Equipment
                </IonLabel>
                <IonSelect
                  interface="popover"
                  disabled={disableInsp}
                  placeholder="Select testing equipment(s)"
                  multiple={true}
                  selectedText={(() => {
                    const parsed = safeJsonParse<TestEquipment[]>(
                      currentTestEquip,
                      []
                    );
                    if (!Array.isArray(parsed) || parsed.length === 0) {
                      return "";
                    }
                    return parsed
                      .map((t) => t?.Equipment)
                      .filter(Boolean)
                      .join(", ");
                  })()}
                  onIonChange={(ev) => {
                    markDirty();
                    setCurrentTestEquip(JSON.stringify(ev.detail.value));
                  }}
                >
                  {(testEquipmentList ?? []).map((equip) => (
                    <IonSelectOption key={equip.Equipment} value={equip}>
                      {equip.Equipment +
                        " (Exp:" +
                        new Date(
                          equip.CalibrationExpiresDate
                        ).toLocaleDateString() +
                        ")"}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
          </IonCard>
        )}

        <IonCard>
          <IonCardContent className="inspection-ioncard-content">
            {pointEquipment.length === 1 &&
            pointEquipment[0].Equipment === "No Equipment" ? (
              <IonGrid>{renderPointRows(pointDefs)}</IonGrid>
            ) : (
              <IonAccordionGroup>
                {pointEquipment.map((PointEqDescription, index) => (
                  <IonAccordion
                    key={`${index}${PointEqDescription.Equipment}`}
                    value={PointEqDescription.Equipment}
                  >
                    <IonItem slot="header">
                      <IonLabel>
                        {PointEqDescription.Equipment === "No Equipment"
                          ? translations["lbl_no_equipment"] || "No Equipment"
                          : `${PointEqDescription.Equipment} - ${PointEqDescription.Description}`}
                      </IonLabel>
                    </IonItem>
                    <div className="ion-padding" slot="content">
                      <IonGrid>
                        {renderPointRows(
                          pointDefs.filter(
                            (p) => p.Equipment === PointEqDescription.Equipment
                          )
                        )}
                      </IonGrid>
                    </div>
                  </IonAccordion>
                ))}
              </IonAccordionGroup>
            )}
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardContent className="inspection-ioncard-content">
            <IonItem fill="outline" hidden={true} color="danger">
              <IonTextarea
                className="inspection-check-label"
                color="danger"
                readonly
                autoGrow
                value={validationErrors.map((p) => p.error).join("\r\n")}
              />
            </IonItem>

            <IonItem disabled={disableInsp} fill="outline">
              <IonTextarea
                className="inspection-check-label"
                readonly
                autoGrow
                value={
                  translatedMessages["ICFailureFlagMessage"]?.MessageText ||
                  "This Inspection could not be completed within specifications."
                }
              />
              <IonCheckbox
                slot="start"
                checked={checkOverride}
                onIonChange={(ev) => {
                  setCheckOverride(ev.detail.checked);
                }}
              />
            </IonItem>

            <IonItem disabled={disableInsp} fill="outline">
              <IonLabel position="floating">
                {translations["lbl_category_comments"] || "Comments"}
              </IonLabel>
              <IonTextarea
                id="txtComments"
                placeholder={
                  translations["lbl_enter comments"] || "Enter Comments"
                }
                autoGrow={true}
                value={comments}
                onIonChange={(ev) => {
                  markDirty();
                  setComments(String(ev.detail.value));
                }}
              />
            </IonItem>
          </IonCardContent>
        </IonCard>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              color="primary"
              fill="solid"
              disabled={isCompleteDisabled}
              onClick={completeInspectionResults}
            >
              {translations["lbl_btn_complete"] || "Complete"}
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              color="primary"
              fill="solid"
              disabled={disableInsp}
              onClick={saveInspectionResultsToLocal}
            >
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
      <IonModal
        isOpen={showCommentModal}
        onDidDismiss={() => setShowCommentModal(false)}
        breakpoints={[0, 0.5, 0.8]}
        initialBreakpoint={0.5}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {translations["lbl_enter_comment"] || "Enter Comment"}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowCommentModal(false)}>
                <IonIcon icon={close}></IonIcon>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonItem>
            <IonLabel position="stacked">
              {translations["lbl_comment"] || "Comment"}
            </IonLabel>
            <IonTextarea
              placeholder={
                translations["lbl_enter_comment"] ||
                "Enter your comment here..."
              }
              rows={4}
              value={tempCommentInput}
              onIonChange={(e) => setTempCommentInput(e.detail.value || "")}
            />
          </IonItem>
          <IonItem lines="none" className="ion-padding">
            <IonButton expand="block" onClick={handleCommentSave}>
              {translations["lbl_save_comment"] || "Save Comment"}
            </IonButton>
          </IonItem>
        </IonContent>
      </IonModal>

      <IonModal
        isOpen={showDescriptionModal}
        onDidDismiss={() => setShowDescriptionModal(false)}
        breakpoints={[0, 0.3, 0.5, 0.7]}
        initialBreakpoint={0.5}
        handle={true}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {translations["lbl_description"] || "Description"}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                fill="clear"
                onClick={() => setShowDescriptionModal(false)}
              >
                {translations["lbl_btn_close"] || "Close"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div
            style={{
              whiteSpace: "pre-wrap",
              lineHeight: "1.6",
              fontSize: "14px",
              color: "var(--ion-color-dark)",
              padding: "16px 0",
            }}
          >
            {fullDescription}
          </div>
        </IonContent>
      </IonModal>

      <IonModal
        isOpen={showLongDescriptionModal}
        onDidDismiss={() => setShowLongDescriptionModal(false)}
        breakpoints={longDescriptionModalBreakpoints}
        initialBreakpoint={longDescriptionModalBreakpoint}
        handle={true}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {translations["lbl_long_description"] || "Long Description"}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                fill="clear"
                onClick={() => setShowLongDescriptionModal(false)}
              >
                {translations["lbl_btn_close"] || "Close"}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent scrollY>
          <IonItem
            lines="none"
            className="inspection-long-description-modal-item"
          >
            <IonLabel>
              <IonText className="inspection-long-description-modal-text">
                {htmlParse(fullLongDescription)}
              </IonText>
            </IonLabel>
          </IonItem>
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

export default Inspection;
